import React from 'react'

// definitions
import { AccountStripeOrgWithData, AccountXeroOrgWithData } from '../../../../screens/subsync/subsync'
import { StripeOrgAccountXeroOrgMapping, StripeMappingType } from '../../../../../../back-end/utilities/apiDefinitions'
import { MessageAction } from '../../../ui/messages/message'
import { AppState } from '../../../../App.d'
import { GeneralSettings } from './GeneralSettings'
import { CreditNotesSettings } from './CreditNotesSettings'
import { DiscountsSettings } from './DiscountsSettings'
import { TaxSettings } from './TaxSettings'
import { StripeFeesSettings } from './StripeFeesSettings'
import { ProductSettings } from './ProductSettings'
import { CustomMappings } from './CustomMappings'
import { InvoicePeriodsSettings } from './InvoicePeriods'
import { CustomerBalanceSettings } from './CustomerBalanceSettings'
import { PayoutSettings } from './PayoutSettings'
import { DetailedStripeOrgAccountXeroOrg, Error } from '../settings'
import { PaymentsSettings } from './PaymentsSettings'
import SettingsCard from './SettingsCard'
import { ShippingSettings } from './ShippingSettings'
import { StripeOrgAccountXeroOrgSettings } from '../../../../../../back-end/common/stripeOrgAccountXeroOrgSettings'

interface OrgMappingContentProps {
	stripeOrg: AccountStripeOrgWithData
	orgMapping: DetailedStripeOrgAccountXeroOrg | undefined
	mappedXeroOrg: AccountXeroOrgWithData | undefined
	stripeMappingTypes: StripeMappingType[]
	xeroOrgs: AccountXeroOrgWithData[]
	appState: AppState
	handleOrgUpdate: (stripeOrgID: string, name: string, value: string) => void
	handleProductUpdate: (stripeOrgID: string, stripeProductID: string, name: string, value: string) => void
	handleProductAdd: (stripeOrgID: string, stripeProductID: string, name: string, value: string) => void
	handleApplyToAllProducts: (stripeOrgID: string, name: string, value: string) => void
	handleTaxRateUpdate: (stripeOrgID: string, stripeTaxRateID: string, name: string, value: string) => void
	handleTaxRateAdd: (stripeOrgID: string, stripeTaxRateID: string, name: string, value: string) => void
	handleSettingsUpdate: (stripeOrgID: string, values: Partial<StripeOrgAccountXeroOrgSettings>) => void
	handleCustomMappingUpdate: (stripeOrgID: string, customMappings: Partial<StripeOrgAccountXeroOrgMapping>[]) => void
	updateMessages: (action: MessageAction) => void
	errors: Error[]
}

const OrgMappingContent = (props: OrgMappingContentProps) => {
	if (!(props.orgMapping && props.mappedXeroOrg)) {
		return null
	}
	return (
		<div className="mt-5">
			<div
				className={`mb-2 ${
					props.errors.some((error) => error.element === 'general-settings-mapping' && error.stripeID === props.stripeOrg.stripeOrg!.stripeOrgID!)
						? 'highlight-subsync-settings-error'
						: ''
				}`}
			>
				{props.errors
					.filter((error) => error.element === 'general-settings-mapping' && error.stripeID === props.stripeOrg.stripeOrg!.stripeOrgID!)
					.map((error, index) => (
						<div className="error-message" key={index}>
							{error.message}
						</div>
					))}
				<SettingsCard
					title="General Settings"
					helpLink="https://help.finlert.com/subsync/general-settings"
					startsCollapsed={false}
					collapsibleElement={
						<GeneralSettings
							orgMapping={props.orgMapping}
							stripeOrgID={props.stripeOrg.stripeOrg!.stripeOrgID!}
							mappedXeroOrg={props.mappedXeroOrg}
							handleOrgUpdate={props.handleOrgUpdate}
							handleSettingsUpdate={props.handleSettingsUpdate}
							errors={props.errors.filter(
								(error) => error.element === 'general-settings-mapping' && error.stripeID === props.stripeOrg.stripeOrg!.stripeOrgID!
							)}
						/>
					}
				/>
			</div>
			{props.orgMapping.stripeOrgAccountXeroOrgProcessingType === 'Rollup' && (
				<div
					className={`mb-2 ${
						props.errors.some((error) => error.element === 'payout-mapping' && error.stripeID === props.stripeOrg.stripeOrg!.stripeOrgID!)
							? 'highlight-subsync-settings-error'
							: ''
					}`}
				>
					{props.errors
						.filter((error) => error.element === 'payout-mapping' && error.stripeID === props.stripeOrg.stripeOrg!.stripeOrgID!)
						.map((error, index) => (
							<div className="error-message" key={index}>
								{error.message}
							</div>
						))}
					<SettingsCard
						title="Payouts"
						helpLink="https://help.finlert.com/subsync/summary-mode"
						startsCollapsed={false}
						collapsibleElement={
							<PayoutSettings
								stripeOrgID={props.stripeOrg.stripeOrg!.stripeOrgID!}
								orgMapping={props.orgMapping!}
								mappedXeroOrg={props.mappedXeroOrg}
								appState={props.appState}
								handleSettingsUpdate={props.handleSettingsUpdate}
								updateMessages={props.updateMessages}
							/>
						}
					/>
				</div>
			)}
			<div
				className={`mb-2 ${
					props.errors.some((error) => error.element === 'account-product-mapping' && error.stripeID === props.stripeOrg.stripeOrg!.stripeOrgID!)
						? 'highlight-subsync-settings-error'
						: ''
				}`}
			>
				<SettingsCard
					title="Account/Product Mapping"
					helpLink="https://help.finlert.com/subsync/account-product-mapping"
					startsCollapsed={false}
					collapsibleElement={
						<ProductSettings
							stripeOrgID={props.stripeOrg.stripeOrg!.stripeOrgID!}
							orgMapping={props.orgMapping!}
							stripeProducts={props.stripeOrg.products}
							types={['Account', 'Item']}
							customMappings={props.orgMapping!.customMappings!}
							mappedXeroOrg={props.mappedXeroOrg}
							stripeMappingTypes={props.stripeMappingTypes}
							handleOrgUpdate={props.handleOrgUpdate}
							handleCustomMappingUpdate={props.handleCustomMappingUpdate}
							handleSettingsUpdate={props.handleSettingsUpdate}
							handleProductUpdate={props.handleProductUpdate}
							handleProductAdd={props.handleProductAdd}
							handleApplyToAllProducts={props.handleApplyToAllProducts}
							errors={props.errors.filter(
								(error) => error.element === 'account-product-mapping' && error.stripeID === props.stripeOrg.stripeOrg!.stripeOrgID!
							)}
						/>
					}
				/>
			</div>
			<div></div>
			<div
				className={`mb-2 ${
					props.errors.some((error) => error.element === 'tax-settings-mapping' && error.stripeID === props.stripeOrg.stripeOrg!.stripeOrgID!)
						? 'highlight-subsync-settings-error'
						: ''
				}`}
			>
				<SettingsCard
					title="Tax Settings"
					helpLink="https://help.finlert.com/subsync/tax-configuration"
					startsCollapsed={false}
					collapsibleElement={
						<TaxSettings
							orgMapping={props.orgMapping}
							mappedXeroOrg={props.mappedXeroOrg}
							stripeOrgID={props.stripeOrg.stripeOrg!.stripeOrgID!}
							stripeTaxRates={props.stripeOrg.taxRates}
							handleSettingsUpdate={props.handleSettingsUpdate}
							handleTaxRateUpdate={props.handleTaxRateUpdate}
							handleTaxRateAdd={props.handleTaxRateAdd}
							errors={props.errors.filter(
								(error) => error.element === 'tax-settings-mapping' && error.stripeID === props.stripeOrg.stripeOrg!.stripeOrgID!
							)}
						/>
					}
				/>
			</div>
			<div
				className={`mb-2 ${
					props.errors.some((error) => error.element === 'tracking-categories' && error.stripeID === props.stripeOrg.stripeOrg!.stripeOrgID!)
						? 'highlight-subsync-settings-error'
						: ''
				}`}
			>
				{props.errors
					.filter((error) => error.element === 'tracking-categories' && error.stripeID === props.stripeOrg.stripeOrg!.stripeOrgID!)
					.map((error, index) => (
						<div className="error-message" key={index}>
							{error.message}
						</div>
					))}
				<SettingsCard
					title="Tracking Categories"
					helpLink="https://help.finlert.com/subsync/xero-tracking-categories"
					startsCollapsed={true}
					collapsibleElement={
						<CustomMappings
							stripeOrgID={props.stripeOrg.stripeOrg!.stripeOrgID!}
							orgMapping={props.orgMapping!}
							types={['TrackingCategoryOption']}
							customMappings={props.orgMapping!.customMappings!}
							mappedXeroOrg={props.mappedXeroOrg}
							stripeMappingTypes={props.stripeMappingTypes}
							handleCustomMappingUpdate={props.handleCustomMappingUpdate}
							handleSettingsUpdate={props.handleSettingsUpdate}
						/>
					}
				/>
			</div>
			<div
				className={`mb-2 ${
					props.errors.some((error) => error.element === 'stripe-fees-mapping' && error.stripeID === props.stripeOrg.stripeOrg!.stripeOrgID!)
						? 'highlight-subsync-settings-error'
						: ''
				}`}
			>
				{props.errors
					.filter((error) => error.element === 'stripe-fees-mapping' && error.stripeID === props.stripeOrg.stripeOrg!.stripeOrgID!)
					.map((error, index) => (
						<div className="error-message" key={index}>
							{error.message}
						</div>
					))}
				<SettingsCard
					title="Stripe Fees"
					helpLink="https://help.finlert.com/subsync/stripe-fees"
					startsCollapsed={true}
					collapsibleElement={
						<StripeFeesSettings
							orgMapping={props.orgMapping}
							mappedXeroOrg={props.mappedXeroOrg}
							stripeOrgID={props.stripeOrg.stripeOrg!.stripeOrgID!}
							xeroOrgs={props.xeroOrgs}
							appState={props.appState}
							handleSettingsUpdate={props.handleSettingsUpdate}
							updateMessages={props.updateMessages}
						/>
					}
				/>
			</div>
			<div
				className={`mb-2 ${
					props.errors.some((error) => error.element === 'credit-notes-mapping' && error.stripeID === props.stripeOrg.stripeOrg!.stripeOrgID!)
						? 'highlight-subsync-settings-error'
						: ''
				}`}
			>
				{props.errors
					.filter((error) => error.element === 'credit-notes-mapping' && error.stripeID === props.stripeOrg.stripeOrg!.stripeOrgID!)
					.map((error, index) => (
						<div className="error-message" key={index}>
							{error.message}
						</div>
					))}
				<SettingsCard
					title="Credit Notes"
					helpLink="https://help.finlert.com/subsync/credit-notes"
					startsCollapsed={true}
					collapsibleElement={
						<CreditNotesSettings
							orgMapping={props.orgMapping}
							mappedXeroOrg={props.mappedXeroOrg}
							stripeOrgID={props.stripeOrg.stripeOrg!.stripeOrgID!}
							handleSettingsUpdate={props.handleSettingsUpdate}
						/>
					}
				/>
			</div>
			<div
				className={`mb-2 ${
					props.errors.some((error) => error.element === 'customer-balance-mapping' && error.stripeID === props.stripeOrg.stripeOrg!.stripeOrgID!)
						? 'highlight-subsync-settings-error'
						: ''
				}`}
			>
				{props.errors
					.filter((error) => error.element === 'customer-balance-mapping' && error.stripeID === props.stripeOrg.stripeOrg!.stripeOrgID!)
					.map((error, index) => (
						<div className="error-message" key={index}>
							{error.message}
						</div>
					))}
				<SettingsCard
					title="Customer Balance"
					helpLink="https://help.finlert.com/subsync/customer-balance"
					startsCollapsed={true}
					collapsibleElement={
						<CustomerBalanceSettings
							orgMapping={props.orgMapping}
							mappedXeroOrg={props.mappedXeroOrg}
							stripeOrgID={props.stripeOrg.stripeOrg!.stripeOrgID!}
							handleSettingsUpdate={props.handleSettingsUpdate}
						/>
					}
				/>
			</div>
			<div
				className={`mb-2 ${
					props.errors.some((error) => error.element === 'discounts-mapping' && error.stripeID === props.stripeOrg.stripeOrg!.stripeOrgID!)
						? 'highlight-subsync-settings-error'
						: ''
				}`}
			>
				{props.errors
					.filter((error) => error.element === 'discounts-mapping' && error.stripeID === props.stripeOrg.stripeOrg!.stripeOrgID!)
					.map((error, index) => (
						<div className="error-message" key={index}>
							{error.message}
						</div>
					))}
				<SettingsCard
					title="Discounts"
					helpLink="https://help.finlert.com/subsync/discounts"
					startsCollapsed={true}
					collapsibleElement={
						<DiscountsSettings
							orgMapping={props.orgMapping}
							mappedXeroOrg={props.mappedXeroOrg}
							stripeOrgID={props.stripeOrg.stripeOrg!.stripeOrgID!}
							handleSettingsUpdate={props.handleSettingsUpdate}
						/>
					}
				/>
			</div>
			<div
				className={`mb-2 ${
					props.errors.some((error) => error.element === 'invoice-periods-mapping' && error.stripeID === props.stripeOrg.stripeOrg!.stripeOrgID!)
						? 'highlight-subsync-settings-error'
						: ''
				}`}
			>
				{props.errors
					.filter((error) => error.element === 'invoice-periods-mapping' && error.stripeID === props.stripeOrg.stripeOrg!.stripeOrgID!)
					.map((error, index) => (
						<div className="error-message" key={index}>
							{error.message}
						</div>
					))}
				<SettingsCard
					title="Invoice Periods"
					helpLink="https://help.finlert.com/subsync/invoice-periods"
					startsCollapsed={true}
					collapsibleElement={
						<InvoicePeriodsSettings
							orgMapping={props.orgMapping}
							mappedXeroOrg={props.mappedXeroOrg}
							stripeOrgID={props.stripeOrg.stripeOrg!.stripeOrgID!}
							handleSettingsUpdate={props.handleSettingsUpdate}
						/>
					}
				/>
			</div>
			<div
				className={`mb-2 ${
					props.errors.some((error) => error.element === 'payments-mapping' && error.stripeID === props.stripeOrg.stripeOrg!.stripeOrgID!)
						? 'highlight-subsync-settings-error'
						: ''
				}`}
			>
				{props.errors
					.filter((error) => error.element === 'payments-mapping' && error.stripeID === props.stripeOrg.stripeOrg!.stripeOrgID!)
					.map((error, index) => (
						<div className="error-message" key={index}>
							{error.message}
						</div>
					))}
				<SettingsCard
					title="Payments"
					helpLink="https://help.finlert.com/subsync/payments"
					startsCollapsed={true}
					collapsibleElement={
						<PaymentsSettings
							orgMapping={props.orgMapping}
							mappedXeroOrg={props.mappedXeroOrg}
							stripeOrgID={props.stripeOrg.stripeOrg!.stripeOrgID!}
							xeroOrgs={props.xeroOrgs}
							appState={props.appState}
							handleSettingsUpdate={props.handleSettingsUpdate}
							updateMessages={props.updateMessages}
						/>
					}
				/>
			</div>
			<div
				className={`mb-2 ${
					props.errors.some((error) => error.element === 'shipping-mapping' && error.stripeID === props.stripeOrg.stripeOrg!.stripeOrgID!)
						? 'highlight-subsync-settings-error'
						: ''
				}`}
			>
				{props.errors
					.filter((error) => error.element === 'shipping-mapping' && error.stripeID === props.stripeOrg.stripeOrg!.stripeOrgID!)
					.map((error, index) => (
						<div className="error-message" key={index}>
							{error.message}
						</div>
					))}
				<SettingsCard
					title="Shipping"
					helpLink="https://help.finlert.com/subsync/shipping"
					startsCollapsed={true}
					collapsibleElement={
						<ShippingSettings
							orgMapping={props.orgMapping}
							mappedXeroOrg={props.mappedXeroOrg}
							stripeOrgID={props.stripeOrg.stripeOrg!.stripeOrgID!}
							handleSettingsUpdate={props.handleSettingsUpdate}
						/>
					}
				/>
			</div>
		</div>
	)
}

export { OrgMappingContent }
